import type { GeocodeLocation } from '../components/LocationSearchBar/LocationAutocomplete';
import type { MatchableAddress } from '../types';

export function mailingAddressHasValidGeolocation(
  mailingAddress?: {
    geolocationLatitude?: string | null;
    geolocationLongitude?: string | null;
  } | null,
): mailingAddress is {
  geolocationLatitude: string;
  geolocationLongitude: string;
} {
  return Boolean(
    mailingAddress?.geolocationLatitude &&
      mailingAddress.geolocationLongitude &&
      !Number.isNaN(Number(mailingAddress.geolocationLatitude)) &&
      !Number.isNaN(Number(mailingAddress.geolocationLongitude)),
  );
}

export function isStreetAddress(location: GeocodeLocation) {
  return (
    location.types.includes('street_address') ||
    location.types.includes('premise') ||
    location.types.includes('establishment')
  );
}

function matchesStreetAddress(
  location: GeocodeLocation,
  coordinates: google.maps.LatLngLiteral,
) {
  // Google may not have a bounds for street addresses, and the bounds for other location types are not always accurate.
  // As such, we create our own bounds encompassing a 150m radius circle around the location for these location types.
  // NOTE: We can't compare the lat/lng directly since the data source for identity coordinates is not Google Places.
  const circle = new google.maps.Circle({
    center: location.geometry.location,
    radius: 150,
  });
  return circle.getBounds()?.contains(coordinates);
}

function stripCountySuffix(value: string | null) {
  return value?.replaceAll('County', '').trim();
}

export function matches(
  location: GeocodeLocation | null,
  address?: MatchableAddress | null,
) {
  if (!address) {
    return false;
  }

  if (!location?.types.length || !location.address_components.length) {
    return true;
  }

  if (isStreetAddress(location)) {
    return matchesStreetAddress(location, {
      lat: address.geolocationLatitude,
      lng: address.geolocationLongitude,
    });
  }

  switch (location.types[0]) {
    case 'locality':
      return location.address_components[0].long_name === address.city;
    case 'postal_code':
      return (
        location.address_components[0].long_name ===
        address.postalCode?.split('-')[0]
      );
    case 'administrative_area_level_1':
      return location.address_components[0].short_name === address.state;
    case 'administrative_area_level_2':
      return (
        stripCountySuffix(location.address_components[0].long_name) ===
        stripCountySuffix(address.county)
      );
    default:
      return location.geometry.viewport.contains({
        lat: address.geolocationLatitude,
        lng: address.geolocationLongitude,
      });
  }
}

export function matchesGeocoderResult(
  location: GeocodeLocation | null,
  geocodeResult: google.maps.GeocoderResult,
) {
  if (!location?.types.length || !location.address_components.length) {
    return true;
  }

  if (isStreetAddress(location)) {
    return matchesStreetAddress(location, {
      lat: geocodeResult.geometry.location.lat(),
      lng: geocodeResult.geometry.location.lng(),
    });
  }

  switch (location.types[0]) {
    case 'locality':
      return (
        location.address_components[0].long_name ===
        geocodeResult.address_components.find(({ types }) =>
          types.includes('locality'),
        )?.long_name
      );
    case 'postal_code':
      return (
        location.address_components[0].long_name ===
        geocodeResult.address_components.find(({ types }) =>
          types.includes('postal_code'),
        )?.long_name
      );
    case 'administrative_area_level_1':
      return (
        location.address_components[0].short_name ===
        geocodeResult.address_components.find(({ types }) =>
          types.includes('administrative_area_level_1'),
        )?.short_name
      );
    case 'administrative_area_level_2':
      return (
        location.address_components[0].long_name ===
        geocodeResult.address_components.find(({ types }) =>
          types.includes('administrative_area_level_2'),
        )?.long_name
      );
    default:
      return location.geometry.viewport.contains(
        geocodeResult.geometry.location.toJSON(),
      );
  }
}
